import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/Shared/PageHeader';
import { IstiftaLinksTable } from '../components/IstiftaLinks/IstiftaLinksTable';
import { IstiftaLinkModal } from '../components/IstiftaLinks/IstiftaLinkModal';
import { DeleteConfirmDialog } from '../components/IstiftaLinks/DeleteConfirmDialog';
import { Loading } from '../components/Shared/Loading/Loading';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';

import {
  useGetIstiftaLinksQuery,
  useGetIstiftaLinkCategoriesQuery,
  useCreateIstiftaLinkMutation,
  useUpdateIstiftaLinkMutation,
  useDeleteIstiftaLinkMutation,
  useReorderIstiftaLinksMutation,
} from '../redux/api/istiftaLinksApi';

import { IIstiftaLink } from '../common/interfaces/IstiftaLinks.interface';
import { InterestGender } from '../common/enums/InterestGender';
import { usePageTitle } from '../hooks/usePageTitle';

interface LinkFormValues {
  descriptionAr: string;
  descriptionEn: string;
  url: string;
  categoryKey: string;
  interestGender: InterestGender;
}

export const IstiftaLinksPage: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle(t('istiftaLinks.pageTitle'));

  const {
    data: links = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useGetIstiftaLinksQuery();
  const { data: categories = [] } = useGetIstiftaLinkCategoriesQuery();

  const [createIstiftaLink, { isLoading: isCreating }] =
    useCreateIstiftaLinkMutation();
  const [updateIstiftaLink, { isLoading: isUpdating }] =
    useUpdateIstiftaLinkMutation();
  const [deleteIstiftaLink, { isLoading: isDeleting }] =
    useDeleteIstiftaLinkMutation();
  const [reorderIstiftaLinks] = useReorderIstiftaLinksMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<IIstiftaLink | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IIstiftaLink | null>(null);
  const [localLinks, setLocalLinks] = useState<IIstiftaLink[]>([]);
  const [isOptimistic, setIsOptimistic] = useState(false);

  if (isLoading) return <Loading />;
  if (isError) return <ErrorComponent error={error} onRetry={refetch} />;

  const displayLinks = isOptimistic ? localLinks : links;

  const handleAddClick = () => {
    setSelectedLink(null);
    setModalOpen(true);
  };

  const handleEdit = (link: IIstiftaLink) => {
    setSelectedLink(link);
    setModalOpen(true);
  };

  const toDto = (values: LinkFormValues) => ({
    description: { ar: values.descriptionAr, en: values.descriptionEn },
    url: values.url,
    categoryKey: values.categoryKey,
    interestGender: values.interestGender,
  });

  const handleSave = async (values: LinkFormValues) => {
    if (selectedLink) {
      await updateIstiftaLink({ id: selectedLink.id, ...toDto(values) });
    } else {
      await createIstiftaLink({ ...toDto(values), order: links.length + 1 });
    }
    setModalOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteIstiftaLink(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleReorder = async (ids: string[]) => {
    const reordered = ids
      .map((id) => links.find((l) => l.id === id))
      .filter((l): l is IIstiftaLink => Boolean(l))
      .map((l, i) => ({ ...l, order: i + 1 }));
    setLocalLinks(reordered);
    setIsOptimistic(true);
    try {
      await reorderIstiftaLinks({ ids }).unwrap();
    } catch {
      setIsOptimistic(false);
    } finally {
      setIsOptimistic(false);
    }
  };

  return (
    <div className="p-6">
      <PageHeader
        title={t('istiftaLinks.pageTitle')}
        buttonText={t('istiftaLinks.addLink')}
        buttonOnClick={handleAddClick}
      />

      <IstiftaLinksTable
        links={displayLinks}
        categories={categories}
        isReordering={isOptimistic}
        onEdit={handleEdit}
        onDelete={setDeleteTarget}
        onReorder={handleReorder}
      />

      <IstiftaLinkModal
        isOpen={modalOpen}
        initialValues={selectedLink ?? undefined}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        isSaving={isCreating || isUpdating}
      />

      {deleteTarget && (
        <DeleteConfirmDialog
          isOpen={Boolean(deleteTarget)}
          linkDescription={deleteTarget.description.ar}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};
